import FormField from '@/components/ui/FormField'
import ChangeShippingTypeDialog from './ChangeShippingTypeDialog'
import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import { deriveOrderLabel } from '@/components/shared/OrderSubscriptionTypeChip'
import { orderStatusValueToLabel } from '@/constants/statuses'
import { useUnsavedChangesProcessing } from '@/hooks/useUnsavedChangesProcessing'
import { type MedReqListItem, useMedReqsByOrderHashQuery } from '@/services/med-requests'
import {
  useCancelShipmentMutation,
  useChangeableShippingMethodsQuery,
  useCreateShipmentMutation,
  usePrintOrderLabelsMutation,
  useSendToTitan,
  useShipmentPackagesByOrderIdQuery,
  useUpdateOrderBillingShippingMutation,
  useUpdateOrderCancellationReasonMutation,
  useUpdateOrderShippingMethodMutation,
  type BillingShippingFields
} from '@/services/orders'
import { borderRadius, colors, sxPresets } from '@/theme'
import { CANCELLATION_REASONS, CANCELLATION_REASON_MAX_LENGTH, type Order } from '@/types/order'
import { AddCircle, Cancel, Edit as EditIcon, Share } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { zodResolver } from '@hookform/resolvers/zod'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'

interface OrderDataProps {
  order: Order
  readOnly?: boolean
}

function Field({
  label,
  value,
  capitalize,
  asUrl,
  size = 3
}: {
  label: string
  value?: string | number | null
  capitalize?: boolean
  asUrl?: string
  size?: number
}) {
  const isValueEmpty = value === null || value === undefined || value === ''
  const renderTextValue = () => (
    <Typography
      sx={{
        color: colors.text.dark,
        fontSize: '14px',
        fontWeight: 400,
        textTransform: capitalize ? 'capitalize' : undefined,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {isValueEmpty ? '-' : value}
    </Typography>
  )

  return (
    <Grid size={size}>
      <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>{label}</Typography>
      <Tooltip title={isValueEmpty ? '' : String(value)} disableHoverListener={isValueEmpty}>
        <Box>
          {asUrl ? (
            <Link to={asUrl} target='_blank' style={{ color: colors.text.primary }}>
              {renderTextValue()}
            </Link>
          ) : (
            renderTextValue()
          )}
        </Box>
      </Tooltip>
    </Grid>
  )
}

const FieldRow = ({ children }: { children: React.ReactNode }) => (
  <Grid
    container
    sx={{
      '& > *:not(:last-child)': {
        borderRight: `1px solid ${colors.border.default}`,
        pr: 3
      },
      '& > *:not(:first-child)': { pl: 3 }
    }}
  >
    {children}
  </Grid>
)

const sectionStyles = {
  p: 3,
  borderRadius: borderRadius.lg,
  border: `1px solid ${colors.border.default}`
}

function CTAButton({
  icon: Icon,
  label,
  ...rest
}: {
  icon: React.ElementType
  label: string
} & Omit<React.ComponentProps<typeof Button>, 'startIcon'>) {
  return (
    <Button
      variant='outlined'
      {...rest}
      fullWidth
      sx={{
        gap: '8px',
        borderColor: '#D2D5D9',
        fontSize: '12px',
        fontWeight: 600,
        ...rest.sx
      }}
      color='secondary'
      startIcon={<Icon sx={{ width: '24px', height: '24px' }} />}
    >
      {label}
    </Button>
  )
}

const billingShippingSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  county: z.string(),
  postCode: z.string(),
  country: z.string()
})

type BillingShippingFormData = z.infer<typeof billingShippingSchema>

/**
 * The shipping form carries the shipping method on top of the address the billing form
 * shares, because the method is stored on the order rather than on either address.
 */
const shippingSchema = billingShippingSchema.extend({
  shippingMethod: z.string()
})

type ShippingFormData = z.infer<typeof shippingSchema>

const contactInfoSchema = z.object({
  email: z.string().email('Invalid email address'),
  phone: z.string(),
  cancellationReason: z
    .string()
    .max(CANCELLATION_REASON_MAX_LENGTH, `Must be ${CANCELLATION_REASON_MAX_LENGTH} characters or fewer`)
})

type ContactInfoFormData = z.infer<typeof contactInfoSchema>

function getDefaultValues(info: Order['shipping'] | Order['billing']): BillingShippingFormData {
  return {
    firstName: info.firstName,
    lastName: info.lastName,
    address1: info.address1,
    address2: info.address2,
    city: info.city,
    county: info.county,
    postCode: info.postCode,
    country: info.country
  }
}

function toApiFields(
  data: BillingShippingFormData,
  dirtyFields: Partial<Record<keyof BillingShippingFormData, boolean>>
): BillingShippingFields {
  const mapping: Record<keyof BillingShippingFormData, keyof BillingShippingFields> = {
    firstName: 'first_name',
    lastName: 'last_name',
    address1: 'address_1',
    address2: 'address_2',
    city: 'city',
    county: 'state',
    postCode: 'postcode',
    country: 'country'
  }

  const result: BillingShippingFields = {}
  for (const key of Object.keys(dirtyFields) as (keyof BillingShippingFormData)[]) {
    if (key in mapping) {
      result[mapping[key]] = data[key]
    }
  }
  return result
}

function getDispensingProductCode(medRequest: MedReqListItem): string | null {
  if (medRequest.ampmVmpp === 'Virtual') return medRequest.vmp
  if (medRequest.ampmVmpp === 'Actual') return medRequest.amp
  return null
}

function getTitanValidationError(medRequests: MedReqListItem[]): string | null {
  const readyMedRequests = medRequests.filter(medRequest => medRequest.orderItemStatus === 'Ready For Fulfillment')
  const itemsWithoutPrescriber = readyMedRequests.filter(medRequest => !medRequest.lastUpdatedBy?.name?.trim())

  if (itemsWithoutPrescriber.length > 0) {
    return `Cannot send to Titan because prescriber information is missing or invalid for item(s): ${itemsWithoutPrescriber.map(item => item.orderItemHash).join(', ')}`
  }

  const itemsWithoutProductCode = readyMedRequests.filter(medRequest => !getDispensingProductCode(medRequest)?.trim())

  if (itemsWithoutProductCode.length > 0) {
    return `Cannot send to Titan because prescribed item AMP/VMP data is missing or invalid for item(s): ${itemsWithoutProductCode.map(item => item.orderItemHash).join(', ')}`
  }

  return null
}

export default function OrderData({ order, readOnly }: OrderDataProps) {
  const {
    data: shipments,
    isLoading: areShipmentsLoading,
    isFetching: areShipmentsFetching,
    refetch: refetchShipments
  } = useShipmentPackagesByOrderIdQuery(order.orderNumber)

  const { data: medRequests = [] } = useMedReqsByOrderHashQuery(order.hash)

  // We're going to pick the latest med request and use the product group of that med request as the order type
  // since the expected order type for a support app viewer is POM / P (OTC) / GSL / INV, which are the possible product groups.
  // const orderType = useMemo(() => {
  //   const latestMedRequest = medRequests.sort(
  //     (a, b) => new Date(b?.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
  //   )[0]
  //   return latestMedRequest?.productGroup?.toUpperCase()
  // }, [medRequests])

  const allMedReqsReadyForFulfilment = medRequests.some(medReq => medReq.orderItemStatus === 'Ready For Fulfillment')

  const printLabelsMutation = usePrintOrderLabelsMutation()
  const createShipmentMutation = useCreateShipmentMutation()
  const cancelShipmentMutation = useCancelShipmentMutation()
  const sendToTitanMutation = useSendToTitan()
  const updateShippingMutation = useUpdateOrderBillingShippingMutation(order.hash)
  const updateShippingMethodMutation = useUpdateOrderShippingMethodMutation(order.hash)
  const updateBillingMutation = useUpdateOrderBillingShippingMutation(order.hash)

  const areShipmentsPending =
    areShipmentsLoading ||
    areShipmentsFetching ||
    printLabelsMutation.isPending ||
    createShipmentMutation.isPending ||
    cancelShipmentMutation.isPending

  // const handlePrintShippingLabel = () => {
  //   printLabelsMutation.mutate(order.hash, {
  //     onSuccess: labels => {
  //       enqueueSnackbar(`Successfully printed ${labels.length} shipping label${labels.length !== 1 ? 's' : ''}`, {
  //         variant: 'success'
  //       })
  //     }
  //   })
  // }

  const handleCreateShipment = () => {
    createShipmentMutation.mutate(order.hash, {
      onSuccess: () => {
        enqueueSnackbar('Shipment created successfully', {
          variant: 'success'
        })
        refetchShipments()
      }
    })
  }

  const handleCancelShipment = () => {
    cancelShipmentMutation.mutate(order.orderNumber, {
      onSuccess: () => {
        enqueueSnackbar('Shipment cancelled successfully', {
          variant: 'success'
        })
        refetchShipments()
      }
    })
  }

  const handleSendToTitan = () => {
    const validationError = getTitanValidationError(medRequests)
    if (validationError) {
      enqueueSnackbar(validationError, { variant: 'error' })
      return
    }

    sendToTitanMutation.mutate(
      {
        orderNumber: order.orderNumber
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Order sent to Titan successfully', {
            variant: 'success'
          })
        }
      }
    )
  }

  // --- Shipping edit mode ---
  const [isShippingEditMode, setIsShippingEditMode] = useState(false)
  const { data: shippingMethods = [] } = useChangeableShippingMethodsQuery()
  const shippingDefaults: ShippingFormData = {
    ...getDefaultValues(order.shipping),
    shippingMethod: order.shippingMethod
  }
  const shippingForm = useForm<ShippingFormData>({
    resolver: zodResolver(shippingSchema),
    defaultValues: shippingDefaults
  })

  // The dropdown offers the methods an order can be moved onto. An order sitting on
  // anything else — a collect method, or a title that predates the current list — keeps its
  // own value as an option so the field shows what it is rather than rendering blank.
  const shippingMethodOptions = useMemo(() => {
    const titles = shippingMethods.map(method => method.title)

    if (!order.shippingMethod || titles.includes(order.shippingMethod)) return titles

    return [order.shippingMethod, ...titles]
  }, [shippingMethods, order.shippingMethod])

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync defaults on order change
  useEffect(() => {
    shippingForm.reset(shippingDefaults)
  }, [order.shipping, order.shippingMethod])

  useUnsavedChangesProcessing(isShippingEditMode && shippingForm.formState.isDirty)

  const handleShippingEdit = () => {
    shippingForm.reset(shippingDefaults)
    setIsShippingEditMode(true)
  }

  const handleShippingCancel = () => {
    setIsShippingEditMode(false)
    shippingForm.reset(shippingDefaults)
  }

  // An order with a live shipment has a label printed on the old shipping type, so changing
  // the type also regenerates the shipment. Delivered and archived packages don't count.
  const hasActiveShipment = shipments?.some(pkg => !pkg.archivedAt && !pkg.hasBeenDelivered) ?? false
  const [pendingShippingChange, setPendingShippingChange] = useState<ShippingFormData | null>(null)

  const onShippingSubmit = async (data: ShippingFormData) => {
    if (readOnly) return

    // Regenerating the label cancels the current one, so confirm before touching anything.
    if (shippingForm.formState.dirtyFields.shippingMethod && hasActiveShipment) {
      setPendingShippingChange(data)
      return
    }

    await saveShipping(data)
  }

  const handleConfirmShippingChange = async () => {
    if (!pendingShippingChange) return
    await saveShipping(pendingShippingChange)
    setPendingShippingChange(null)
  }

  const saveShipping = async (data: ShippingFormData) => {
    const fields = toApiFields(data, shippingForm.formState.dirtyFields)
    const hasAddressChanges = Object.keys(fields).length > 0

    // The dropdown holds titles, since that is what the order stores; the API takes ids.
    const nextMethodId = shippingForm.formState.dirtyFields.shippingMethod
      ? shippingMethods.find(method => method.title === data.shippingMethod)?.method_id
      : undefined

    if (!hasAddressChanges && !nextMethodId) {
      setIsShippingEditMode(false)
      return
    }

    // The address and the shipping method are separate endpoints behind one Save, so fire
    // them together and only leave edit mode once both have landed.
    const results = await Promise.allSettled([
      hasAddressChanges ? updateShippingMutation.mutateAsync({ shipping: fields }) : undefined,
      nextMethodId ? updateShippingMethodMutation.mutateAsync(nextMethodId) : undefined
    ])

    if (results.some(result => result.status === 'rejected')) return

    setIsShippingEditMode(false)

    if (!nextMethodId || !hasActiveShipment) {
      enqueueSnackbar('Shipping details updated successfully', {
        variant: 'success'
      })
      return
    }

    // Same call as the Create shipment button: PHP cancels the current Royal Mail shipment,
    // creates one on the new service, and the Titan label job swaps the label and tag.
    try {
      await createShipmentMutation.mutateAsync(order.hash)
      refetchShipments()
      enqueueSnackbar('Shipping type updated and a new shipping label has been created', {
        variant: 'success'
      })
    } catch {
      enqueueSnackbar(
        'Shipping type updated, but the new label could not be created. Use "Create shipment" to retry.',
        {
          variant: 'warning'
        }
      )
    }
  }

  // --- Billing edit mode ---
  const [isBillingEditMode, setIsBillingEditMode] = useState(false)
  const billingDefaults = getDefaultValues(order.billing)
  const billingForm = useForm<BillingShippingFormData>({
    resolver: zodResolver(billingShippingSchema),
    defaultValues: billingDefaults
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync defaults on order change
  useEffect(() => {
    billingForm.reset(billingDefaults)
  }, [order.billing])

  useUnsavedChangesProcessing(isBillingEditMode && billingForm.formState.isDirty)

  const handleBillingEdit = () => {
    billingForm.reset(billingDefaults)
    setIsBillingEditMode(true)
  }

  const handleBillingCancel = () => {
    setIsBillingEditMode(false)
    billingForm.reset(billingDefaults)
  }

  const onBillingSubmit = (data: BillingShippingFormData) => {
    if (readOnly) return
    const fields = toApiFields(data, billingForm.formState.dirtyFields)
    if (Object.keys(fields).length === 0) {
      setIsBillingEditMode(false)
      return
    }
    updateBillingMutation.mutate(
      { billing: fields },
      {
        onSuccess: () => {
          enqueueSnackbar('Billing details updated successfully', {
            variant: 'success'
          })
          setIsBillingEditMode(false)
        }
      }
    )
  }

  const isShippingSaving =
    updateShippingMutation.isPending || updateShippingMethodMutation.isPending || createShipmentMutation.isPending
  const isBillingSaving = updateBillingMutation.isPending

  // --- Contact info edit mode ---
  const [isContactEditMode, setIsContactEditMode] = useState(false)
  const updateContactMutation = useUpdateOrderBillingShippingMutation(order.hash)
  const updateCancellationReasonMutation = useUpdateOrderCancellationReasonMutation(order.hash)
  const contactDefaults: ContactInfoFormData = {
    email: order.billing.email || order.shipping.email || '',
    phone: order.billing.phone || order.shipping.phone || '',
    cancellationReason: order.cancellationReason ?? ''
  }
  const contactForm = useForm<ContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: contactDefaults
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync defaults on order change
  useEffect(() => {
    contactForm.reset(contactDefaults)
  }, [order.billing.email, order.billing.phone, order.shipping.email, order.shipping.phone, order.cancellationReason])

  useUnsavedChangesProcessing(isContactEditMode && contactForm.formState.isDirty)

  const handleContactEdit = () => {
    contactForm.reset(contactDefaults)
    setIsContactEditMode(true)
  }

  const handleContactCancel = () => {
    setIsContactEditMode(false)
    contactForm.reset(contactDefaults)
  }

  const onContactSubmit = async (data: ContactInfoFormData) => {
    if (readOnly) return
    const fields: BillingShippingFields = {}
    if (contactForm.formState.dirtyFields.email) fields.email = data.email
    if (contactForm.formState.dirtyFields.phone) fields.phone = data.phone

    const hasContactChanges = Object.keys(fields).length > 0
    const hasReasonChange = !!contactForm.formState.dirtyFields.cancellationReason

    if (!hasContactChanges && !hasReasonChange) {
      setIsContactEditMode(false)
      return
    }

    // Contact details and the cancellation reason are separate endpoints behind one
    // Save, so fire them together and only leave edit mode once both have landed.
    const results = await Promise.allSettled([
      hasContactChanges ? updateContactMutation.mutateAsync({ billing: fields, shipping: fields }) : undefined,
      hasReasonChange ? updateCancellationReasonMutation.mutateAsync(data.cancellationReason.trim()) : undefined
    ])

    if (results.some(result => result.status === 'rejected')) return

    enqueueSnackbar('Order details updated successfully', {
      variant: 'success'
    })
    setIsContactEditMode(false)
  }

  const isContactSaving = updateContactMutation.isPending || updateCancellationReasonMutation.isPending

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Order Details */}
      <Paper
        id='order-details'
        elevation={0}
        sx={sectionStyles}
        component='form'
        onSubmit={contactForm.handleSubmit(onContactSubmit)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{
                color: colors.text.dark,
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              Order details
            </Typography>
            {!isContactEditMode && !readOnly && (
              <IconButton onClick={handleContactEdit} size='small' color='primary' type='button'>
                <EditIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            {isContactEditMode && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='primary' type='submit' disabled={!contactForm.formState.isDirty || isContactSaving}>
                  {isContactSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='ghost' onClick={handleContactCancel} type='button' disabled={isContactSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <FieldRow>
            <Field label='Order number (subject)' value={order?.orderNumber} />
            <Field label='Status' value={order.status ? orderStatusValueToLabel(order.status) : '-'} />
            <Field
              label='User name'
              value={
                `${order?.billing?.firstName ?? ''} ${order?.billing?.lastName ?? ''}`.trim() || order?.userDisplayName
              }
            />
            <Field label='Type' value={deriveOrderLabel(order.subscriptionType, order.orderNumber)} />
          </FieldRow>

          <FieldRow>
            {isContactEditMode ? (
              <>
                <Grid size={3}>
                  <FormField
                    name='email'
                    label='Email'
                    control={contactForm.control}
                    isEditMode
                    error={contactForm.formState.errors.email?.message}
                  />
                </Grid>
                <Grid size={3}>
                  <FormField
                    name='phone'
                    label='Mobile'
                    control={contactForm.control}
                    isEditMode
                    error={contactForm.formState.errors.phone?.message}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Field label='Email' value={order.billing.email || order.shipping.email} />
                <Field label='Mobile' value={order.billing.phone || order.shipping.phone} />
              </>
            )}
            <Field label='Medication Request Count' value={medRequests?.length ?? 0} />
          </FieldRow>

          <FieldRow>
            {isContactEditMode ? (
              <Grid size={6}>
                <FormField
                  name='cancellationReason'
                  label='Cancellation reason'
                  control={contactForm.control}
                  isEditMode
                  type='autocomplete'
                  options={[...CANCELLATION_REASONS]}
                  maxLength={CANCELLATION_REASON_MAX_LENGTH}
                  error={contactForm.formState.errors.cancellationReason?.message}
                />
              </Grid>
            ) : (
              <Field label='Cancellation reason' value={order.cancellationReason} size={6} />
            )}
          </FieldRow>
        </Box>
      </Paper>

      {/* Shipping */}
      <Paper
        id='shipping'
        elevation={0}
        sx={sectionStyles}
        component='form'
        onSubmit={shippingForm.handleSubmit(onShippingSubmit)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{
                color: colors.text.dark,
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              Shipping details
            </Typography>
            {!isShippingEditMode && !readOnly && (
              <IconButton onClick={handleShippingEdit} size='small' color='primary' type='button'>
                <EditIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            {isShippingEditMode && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='primary' type='submit' disabled={!shippingForm.formState.isDirty || isShippingSaving}>
                  {isShippingSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='ghost' onClick={handleShippingCancel} type='button' disabled={isShippingSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              '& > *:not(:last-child)': {
                borderRight: isShippingEditMode ? 'none' : `1px solid ${colors.border.default}`,
                pr: isShippingEditMode ? 0 : 3
              }
            }}
          >
            <FormField
              name='firstName'
              label='First name'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.firstName?.message}
            />
            <FormField
              name='lastName'
              label='Last name'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.lastName?.message}
            />
            <FormField
              name='address1'
              label='Address 1'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.address1?.message}
            />
            <FormField
              name='address2'
              label='Address 2'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.address2?.message}
            />
          </Box>

          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              '& > *:not(:last-child)': {
                borderRight: isShippingEditMode ? 'none' : `1px solid ${colors.border.default}`,
                pr: isShippingEditMode ? 0 : 3
              }
            }}
          >
            <FormField
              name='city'
              label='City'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.city?.message}
            />
            <FormField
              name='county'
              label='County'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.county?.message}
            />
            <FormField
              name='postCode'
              label='Post code'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.postCode?.message}
            />
            <FormField
              name='country'
              label='Country'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              error={shippingForm.formState.errors.country?.message}
            />
          </Box>

          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              '& > *:not(:last-child)': {
                borderRight: isShippingEditMode ? 'none' : `1px solid ${colors.border.default}`,
                pr: isShippingEditMode ? 0 : 3
              }
            }}
          >
            <FormField
              name='shippingMethod'
              label='Shipping type'
              control={shippingForm.control}
              isEditMode={isShippingEditMode}
              type='select'
              options={shippingMethodOptions}
              error={shippingForm.formState.errors.shippingMethod?.message}
            />
          </Box>

          {areShipmentsLoading ? (
            <InlineLoadingSpinner text='Loading shipments...' sx={{ marginTop: 4 }} />
          ) : (
            shipments?.map(shipment => (
              <FieldRow key={shipment._id}>
                <Field label='RM Tracking number' value={shipment.trackingNumber} />
                <Field
                  size={6}
                  label='RM Tracking URL'
                  value={shipment.carrierTrackingUrl}
                  asUrl={shipment.carrierTrackingUrl}
                />
              </FieldRow>
            ))
          )}

          {!readOnly && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>Take action</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CTAButton
                  icon={AddCircle}
                  label='Create shipment'
                  onClick={handleCreateShipment}
                  disabled={areShipmentsPending}
                  loading={createShipmentMutation.isPending}
                />
                {/* will be hidden for now, cs might not need this, delete after a month if not used or requested by cs team*/}
                {/*<CTAButton
                icon={Print}
                label='Print shipping label'
                onClick={handlePrintShippingLabel}
                disabled={areShipmentsPending}
              /> */}
                <CTAButton
                  icon={Cancel}
                  label='Cancel Shipment'
                  onClick={handleCancelShipment}
                  disabled={areShipmentsPending}
                />
                <CTAButton
                  icon={Share}
                  onClick={handleSendToTitan}
                  disabled={sendToTitanMutation.isPending || !allMedReqsReadyForFulfilment}
                  loading={sendToTitanMutation.isPending}
                  label='Send to Titan'
                />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper
        id='billing'
        elevation={0}
        sx={sectionStyles}
        component='form'
        onSubmit={billingForm.handleSubmit(onBillingSubmit)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{
                color: colors.text.dark,
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              Billing details
            </Typography>
            {!isBillingEditMode && !readOnly && (
              <IconButton onClick={handleBillingEdit} size='small' color='primary' type='button'>
                <EditIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            {isBillingEditMode && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='primary' type='submit' disabled={!billingForm.formState.isDirty || isBillingSaving}>
                  {isBillingSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='ghost' onClick={handleBillingCancel} type='button' disabled={isBillingSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              '& > *:not(:last-child)': {
                borderRight: isBillingEditMode ? 'none' : `1px solid ${colors.border.default}`,
                pr: isBillingEditMode ? 0 : 3
              }
            }}
          >
            <FormField
              name='firstName'
              label='First name'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.firstName?.message}
            />
            <FormField
              name='lastName'
              label='Last name'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.lastName?.message}
            />
            <FormField
              name='address1'
              label='Address 1'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.address1?.message}
            />
            <FormField
              name='address2'
              label='Address 2'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.address2?.message}
            />
          </Box>

          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              '& > *:not(:last-child)': {
                borderRight: isBillingEditMode ? 'none' : `1px solid ${colors.border.default}`,
                pr: isBillingEditMode ? 0 : 3
              }
            }}
          >
            <FormField
              name='city'
              label='City'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.city?.message}
            />
            <FormField
              name='county'
              label='County'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.county?.message}
            />
            <FormField
              name='postCode'
              label='Post code'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.postCode?.message}
            />
            <FormField
              name='country'
              label='Country'
              control={billingForm.control}
              isEditMode={isBillingEditMode}
              error={billingForm.formState.errors.country?.message}
            />
          </Box>
        </Box>
      </Paper>

      <ChangeShippingTypeDialog
        open={!!pendingShippingChange}
        currentShippingType={order.shippingMethod}
        nextShippingType={pendingShippingChange?.shippingMethod ?? ''}
        isLoading={isShippingSaving}
        onConfirm={handleConfirmShippingChange}
        onClose={() => setPendingShippingChange(null)}
      />
    </Box>
  )
}
